import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import SlideThumbnail from '../components/SlideThumbnail';
import TemplateCardStats from '../components/TemplateCardStats';
import FeatureIcon, { FeatureIconId } from '../components/FeatureIcon';
import { Button, ButtonLink } from '../components/ui/Button';
import { trackDeckCreated, trackTemplatePreviewed } from '../analytics';
import { createDeck } from '../api/decks';
import { useAuth } from '../context/AuthContext';
import { createStarterSlides } from '../data/deckDefaults';
import { saveDesignId } from '../data/designStore';
import { saveActiveDeck } from '../data/deckStore';
import {
  getPitchTemplate,
  getPitchTemplateOrDefault,
  getTemplatePalette,
  visiblePitchTemplates,
} from '../data/pitchTemplates';
import { saveSlides } from '../data/slidesStore';
import { useHomeFaq } from '../i18n/useHomeFaq';
import { usePitchTemplateMeta } from '../i18n/usePitchTemplateMeta';
import { useTemplateStats } from '../hooks/useTemplateStats';
import { appTheme, pitchTheme } from '../styles/theme';

const Page = styled.main`
  flex: 1;
  background: ${appTheme.pageBg};
`;

const Section = styled.section`
  max-width: ${appTheme.maxWidth};
  margin: 0 auto;
  padding: 0 24px;
`;

const HeroSection = styled(Section)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  text-align: flex-start;
  padding-top: 72px;
  padding-bottom: 64px;
  gap: 24px;
`;

const HeroTitle = styled.h1`
  margin: 0;
  font-family: ${pitchTheme.fontPageTitle};
  font-size: clamp(2.25rem, 5vw, 3.25rem);
  font-weight: 400;
  line-height: 1.12;
  letter-spacing: -0.03em;
  color: ${appTheme.greenDark};
  max-width: 16ch;
`;

const HeroIntro = styled.p`
  margin: 0;
  font-size: 1.125rem;
  line-height: 1.65;
  color: ${appTheme.textMuted};
  max-width: 52ch;
`;

const HeroActions = styled.div`
  display: flex;
  gap: 12px;
  width: 300px;
  flex-direction: column;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 8px;
`;

const FeaturesSection = styled(Section)`
  padding-bottom: 80px;
`;

const SectionLead = styled.div`
  text-align: center;
  max-width: 640px;
  margin: 0 auto 40px;
`;

const SectionTitle = styled.h2`
  margin: 0 0 12px;
  font-family: ${pitchTheme.fontPageTitle};
  font-size: clamp(1.375rem, 3vw, 1.75rem);
  font-weight: 500;
  line-height: 1.2;
  letter-spacing: -0.02em;
  color: ${appTheme.text};
`;

const SectionIntro = styled.p`
  margin: 0;
  font-size: 1rem;
  line-height: 1.6;
  color: ${appTheme.textMuted};
`;

const FeatureGrid = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(4, 1fr);

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.article<{ $bg: string; $labelColor: string }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px 20px 20px;
  border-radius: ${appTheme.radiusLg};
  background: ${({ $bg }) => $bg};
  height: 220px;
`;

const FeatureName = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 500;
  color: ${appTheme.text};
`;

const FeatureText = styled.p`
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.55;
  color: ${appTheme.textMuted};
  flex: 1;
`;

const FeatureLabel = styled.span<{ $color: string }>`
  align-self: flex-start;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ $color }) => $color};
  background: rgba(255, 255, 255, 0.65);
  border-radius: ${appTheme.radiusPill};
  padding: 6px 12px;
`;

const HighlightSection = styled(Section)`
  padding-bottom: 80px;
`;

const HighlightCard = styled.div`
  display: grid;
  gap: 32px;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  padding: 48px;
  border-radius: ${appTheme.radiusLg};
  background: ${appTheme.violet};
  color: #fff;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 32px 24px;
  }
`;

const HighlightTitle = styled.h2`
  margin: 0 0 16px;
  font-size: clamp(1.75rem, 4vw, 2.25rem);
  font-weight: 500;
  line-height: 1.15;
  letter-spacing: -0.02em;
`;

const HighlightText = styled.p`
  margin: 0 0 24px;
  font-size: 1rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.78);
`;

const HighlightPreview = styled.div`
  padding: 24px;
  border-radius: ${appTheme.radiusMd};
  background: #fff;
  color: ${appTheme.text};
  box-shadow: ${appTheme.shadowLg};
`;

const PreviewLabel = styled.p`
  margin: 0 0 8px;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${appTheme.textMuted};
`;

const PreviewTitle = styled.p`
  margin: 0 0 12px;
  font-size: 1.125rem;
  font-weight: 500;
  color: ${appTheme.text};
`;

const PreviewList = styled.ul`
  margin: 0;
  padding: 0 0 0 18px;
  font-size: 0.875rem;
  line-height: 1.6;
  color: ${appTheme.textMuted};
`;

const BenefitsSection = styled(Section)`
  padding-bottom: 80px;
`;

const BenefitsHeader = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 40px;
  flex-wrap: wrap;
`;

const BenefitsGrid = styled.div`
  display: grid;
  gap: 32px 48px;
  grid-template-columns: repeat(3, 1fr);

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const BenefitItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const BenefitTitle = styled.h3`
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 500;
  color: ${appTheme.text};
`;

const BenefitText = styled.p`
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.55;
  color: ${appTheme.textMuted};
`;

const TemplatesSection = styled(Section)`
  padding-bottom: 80px;
`;

const TemplatesHeader = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 28px;
  flex-wrap: wrap;
`;

const TemplatesHeaderText = styled.div`
  flex: 1;
  min-width: 240px;
`;

const CarouselNav = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
`;

const ScrollButton = styled.button`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 1px solid ${appTheme.border};
  background: ${appTheme.surface};
  color: ${appTheme.text};
  font-size: 1.25rem;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease, border-color 0.15s ease, opacity 0.15s ease;

  &:hover:not(:disabled) {
    background: ${appTheme.surfaceHover};
    border-color: ${appTheme.green};
  }

  &:focus-visible {
    outline: 2px solid ${appTheme.green};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

const TemplateTrack = styled.div`
  display: flex;
  gap: 30px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  /* Room for TemplateName negative margin (30px) beyond card padding (20px). */
  padding: 4px 12px 12px;
  margin: 0 -12px;
  scroll-padding-inline: 12px;

  &::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none;
`;

const TEMPLATE_CARD_WIDTH = 340;

const TemplateCard = styled.article`
  flex: 0 0 ${TEMPLATE_CARD_WIDTH}px;
  scroll-snap-align: start;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 20px;
  border-radius: ${appTheme.radiusLg};
  border: 1px solid ${appTheme.border};
  background: ${appTheme.surface};
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;

  &:hover {
    border-color: ${appTheme.green};
    box-shadow: ${appTheme.shadowMd};
    transform: translateY(-2px);
  }
`;

const CardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const IndustryBadge = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${appTheme.green};
  background: ${appTheme.mint};
  border-radius: ${appTheme.radiusPill};
  padding: 4px 10px;
`;

const SlideCount = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${appTheme.textMuted};
`;

const TemplateName = styled.h3<{ $color: string }>`
  margin: 0 -30px;
  font-size: 1.0625rem;
  font-weight: 500;
  color: #fff;
  background: ${({ $color }) => $color};
  padding: 10px 30px;
  border-radius: 10px;
`;

const TemplateDescription = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  line-height: 1.5;
  color: ${appTheme.textMuted};
  flex: 1;
`;

const CardActions = styled.div`
  display: flex;
  gap: 8px;
`;

const CardButton = styled(Button)`
  flex: 1;
`;

const CtaBand = styled.section`
  padding: 64px 24px;
  background: ${appTheme.greenDark};
  text-align: center;
`;

const CtaInner = styled.div`
  max-width: 560px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
`;

const CtaTitle = styled.h2`
  margin: 0;
  font-size: clamp(1.5rem, 3vw, 2rem);
  font-weight: 500;
  color: #fff;
  letter-spacing: -0.02em;
`;

const FaqSection = styled(Section)`
  padding-bottom: 80px;
`;

const FaqList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 720px;
  margin: 0 auto;
`;

const FaqItem = styled.details`
  border: 1px solid ${appTheme.border};
  border-radius: ${appTheme.radiusLg};
  background: ${appTheme.surface};
  padding: 0 20px;
  box-shadow: ${appTheme.shadowSm};

  &[open] {
    padding-bottom: 16px;
  }

  &[open] summary::after {
    content: '−';
  }
`;

const FaqQuestion = styled.summary`
  cursor: pointer;
  list-style: none;
  padding: 18px 0;
  font-family: ${pitchTheme.fontPageTitle};
  font-size: 1rem;
  font-weight: 500;
  color: ${appTheme.greenDark};

  &::-webkit-details-marker {
    display: none;
  }

  &::after {
    content: '+';
    float: right;
    font-size: 1.25rem;
    font-weight: 400;
    color: ${appTheme.textMuted};
  }
`;

const FaqAnswer = styled.p`
  margin: 0;
  font-size: 0.9375rem;
  line-height: 1.65;
  color: ${appTheme.textMuted};
`;

const ErrorBanner = styled.p`
  margin: 0 0 24px;
  padding: 12px 16px;
  border-radius: ${appTheme.radiusMd};
  background: ${appTheme.dangerBg};
  border: 1px solid ${appTheme.dangerBorder};
  font-size: 0.875rem;
  font-weight: 600;
  color: ${appTheme.dangerText};
`;

const FEATURE_IDS = ['templates', 'ai', 'editor', 'present'] as const;
const FEATURE_BACKGROUNDS: Record<(typeof FEATURE_IDS)[number], string> = {
  templates: '',
  ai: appTheme.mint,
  editor: appTheme.peach,
  present: '',
};
const FEATURE_LABEL_COLORS: Record<(typeof FEATURE_IDS)[number], string> = {
  templates: appTheme.mintDark,
  ai: appTheme.lavenderDark,
  editor: appTheme.peachDark,
  present: appTheme.orange,
};

const BENEFIT_IDS = ['toolkit', 'formats', 'customizable', 'ai', 'sync', 'founders'] as const;

const TemplatesPage: React.FC = () => {
  const { t } = useTranslation();
  const { getTemplateName, getTemplateDescription, getTemplateIndustry } = usePitchTemplateMeta();
  const { getStat, isLiked, toggleLike, bumpUsage, likingId } = useTemplateStats();
  const homeFaq = useHomeFaq();
  const { user, openLogin } = useAuth();
  const navigate = useNavigate();
  const trackRef = useRef<HTMLDivElement>(null);
  const [cloningTemplateId, setCloningTemplateId] = useState<string | null>(null);
  const [pendingTemplateId, setPendingTemplateId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const updateScrollButtons = useCallback(() => {
    const track = trackRef.current;
    if (!track) {
      return;
    }
    setCanScrollPrev(track.scrollLeft > 4);
    setCanScrollNext(track.scrollLeft + track.clientWidth < track.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) {
      return;
    }
    updateScrollButtons();
    track.addEventListener('scroll', updateScrollButtons, { passive: true });
    const observer = new ResizeObserver(updateScrollButtons);
    observer.observe(track);
    return () => {
      track.removeEventListener('scroll', updateScrollButtons);
      observer.disconnect();
    };
  }, [updateScrollButtons]);

  const scrollTemplates = (direction: 'prev' | 'next'): void => {
    const track = trackRef.current;
    if (!track) {
      return;
    }
    const step = TEMPLATE_CARD_WIDTH + 30;
    track.scrollBy({ left: direction === 'next' ? step : -step, behavior: 'smooth' });
  };

  const cloneTemplateAndEdit = async (templateId: string): Promise<void> => {
    const template = getPitchTemplateOrDefault(templateId);
    setCloningTemplateId(templateId);
    setError(null);
    try {
      const deck = await createDeck({
        name: template.name,
        designId: template.designId,
        slides: createStarterSlides(templateId),
        pitchTemplateId: templateId,
      });
      bumpUsage(templateId);
      saveSlides(deck.slides);
      saveDesignId(deck.designId);
      saveActiveDeck(deck.id, deck.name, deck.designId);
      trackDeckCreated({
        template_id: templateId,
        slide_count: deck.slides.length,
        source: 'templates_page',
      });
      navigate('/edit');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('templates.carousel.createError'));
    } finally {
      setCloningTemplateId(null);
    }
  };

  useEffect(() => {
    if (user && pendingTemplateId) {
      const templateId = pendingTemplateId;
      setPendingTemplateId(null);
      void cloneTemplateAndEdit(templateId);
    }
  }, [user, pendingTemplateId]);

  const previewTemplate = (templateId: string): void => {
    const template = getPitchTemplate(templateId);
    if (!template) {
      return;
    }
    saveSlides(createStarterSlides(templateId));
    saveDesignId(template.designId);
    trackTemplatePreviewed(templateId);
    navigate('/preview', { state: { from: '/' } });
  };

  const useTemplate = (templateId: string): void => {
    if (user === undefined || cloningTemplateId) {
      return;
    }
    if (!user) {
      setPendingTemplateId(templateId);
      openLogin();
      return;
    }
    void cloneTemplateAndEdit(templateId);
  };

  return (
    <Page>
      <HeroSection>
        <div>
        <HeroTitle>{t('templates.hero.title')}</HeroTitle>
        <HeroIntro>{t('templates.hero.intro')}</HeroIntro>
        </div>
        <HeroActions>
          <ButtonLink to="/decks/new" $variant="primary" $size="lg">
            {t('templates.hero.getStarted')}
          </ButtonLink>
          <ButtonLink to="/subscription" $variant="secondary" $size="lg">
            {t('templates.hero.viewPricing')}
          </ButtonLink>
        </HeroActions>
      </HeroSection>

      <FeaturesSection aria-labelledby="features-heading">
        <SectionLead>
          <SectionTitle id="features-heading">
            {t('templates.features.title')}
          </SectionTitle>
          <SectionIntro>{t('templates.features.intro')}</SectionIntro>
        </SectionLead>
        <FeatureGrid>
          {FEATURE_IDS.map((featureId) => (
            <FeatureCard
              key={featureId}
              $bg={FEATURE_BACKGROUNDS[featureId]}
              $labelColor={FEATURE_LABEL_COLORS[featureId]}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

              <FeatureIcon id={featureId as FeatureIconId} />
              <FeatureName>{t(`templates.features.${featureId}.name`)}</FeatureName>
              </div>
          
              <FeatureText>{t(`templates.features.${featureId}.text`)}</FeatureText>
            </FeatureCard>
          ))}
        </FeatureGrid>
      </FeaturesSection>

      <TemplatesSection aria-labelledby="templates-heading">
        <TemplatesHeader>
          <TemplatesHeaderText>
            <SectionTitle id="templates-heading">{t('templates.carousel.title')}</SectionTitle>
            <SectionIntro>{t('templates.carousel.intro')}</SectionIntro>
          </TemplatesHeaderText>
          <CarouselNav aria-label={t('templates.carousel.controls')}>
            <ScrollButton
              type="button"
              aria-label={t('templates.carousel.previous')}
              disabled={!canScrollPrev}
              onClick={() => scrollTemplates('prev')}
            >
              ‹
            </ScrollButton>
            <ScrollButton
              type="button"
              aria-label={t('templates.carousel.next')}
              disabled={!canScrollNext}
              onClick={() => scrollTemplates('next')}
            >
              ›
            </ScrollButton>
          </CarouselNav>
        </TemplatesHeader>

        {error && <ErrorBanner>{error}</ErrorBanner>}

        <TemplateTrack ref={trackRef}>
          {visiblePitchTemplates.map((template) => {
            const palette = getTemplatePalette(template);

            return (
            <TemplateCard key={template.id}>
              {template.sampleSlides[0] && (
                <SlideThumbnail
                  slide={template.sampleSlides[0]}
                  palette={palette}
                />
              )}
              <CardTop>
                <IndustryBadge>{getTemplateIndustry(template)}</IndustryBadge>
                <SlideCount>
                  {t('templates.carousel.slideCount', { count: template.sampleSlides.length })}
                </SlideCount>
              </CardTop>
              <TemplateCardStats
                usageCount={getStat(template.id).usageCount}
                likeCount={getStat(template.id).likeCount}
                liked={isLiked(template.id)}
                liking={likingId === template.id}
                onToggleLike={(event) => {
                  void toggleLike(template.id, event);
                }}
              />
              <TemplateName $color={palette.blue}>{getTemplateName(template)}</TemplateName>
              <TemplateDescription>{getTemplateDescription(template)}</TemplateDescription>
              <CardActions>
                <CardButton type="button" onClick={() => previewTemplate(template.id)}>
                  {t('templates.carousel.preview')}
                </CardButton>
                <CardButton
                  type="button"
                  $variant="primary"
                  disabled={cloningTemplateId === template.id}
                  onClick={() => useTemplate(template.id)}
                >
                  {cloningTemplateId === template.id
                    ? t('templates.carousel.creating')
                    : t('templates.carousel.useTemplate')}
                </CardButton>
              </CardActions>
            </TemplateCard>
            );
          })}
        </TemplateTrack>
      </TemplatesSection>

      <HighlightSection aria-labelledby="ai-heading">
        <HighlightCard>
          <div>
            <HighlightTitle id="ai-heading">{t('templates.highlight.title')}</HighlightTitle>
            <HighlightText>{t('templates.highlight.text')}</HighlightText>
            <ButtonLink to="/decks/new" $variant="accent" $size="md">
              {t('templates.highlight.cta')}
            </ButtonLink>
          </div>
          <HighlightPreview>
            <PreviewLabel>{t('templates.highlight.previewLabel')}</PreviewLabel>
            <PreviewTitle>{t('templates.highlight.previewTitle')}</PreviewTitle>
            <PreviewList>
              {(t('templates.highlight.previewItems', { returnObjects: true }) as string[]).map(
                (item) => (
                  <li key={item}>{item}</li>
                ),
              )}
            </PreviewList>
          </HighlightPreview>
        </HighlightCard>
      </HighlightSection>

      <BenefitsSection aria-labelledby="benefits-heading">
        <BenefitsHeader>
          <SectionTitle id="benefits-heading">{t('templates.benefits.title')}</SectionTitle>
        </BenefitsHeader>
        <BenefitsGrid>
          {BENEFIT_IDS.map((benefitId) => (
            <BenefitItem key={benefitId}>
              <BenefitTitle>{t(`templates.benefits.items.${benefitId}.title`)}</BenefitTitle>
              <BenefitText>{t(`templates.benefits.items.${benefitId}.text`)}</BenefitText>
            </BenefitItem>
          ))}
        </BenefitsGrid>
      </BenefitsSection>

      <FaqSection aria-labelledby="faq-heading">
        <SectionLead>
          <SectionTitle id="faq-heading">{t('templates.faq.title')}</SectionTitle>
          <SectionIntro>{t('templates.faq.intro')}</SectionIntro>
        </SectionLead>
        <FaqList>
          {homeFaq.map((item) => (
            <FaqItem key={item.question}>
              <FaqQuestion>{item.question}</FaqQuestion>
              <FaqAnswer>{item.answer}</FaqAnswer>
            </FaqItem>
          ))}
        </FaqList>
      </FaqSection>

      <CtaBand aria-labelledby="cta-heading">
        <CtaInner>
          <CtaTitle id="cta-heading">{t('templates.cta.title')}</CtaTitle>
          <ButtonLink to="/decks/new" $variant="primary" $size="lg">
            {t('templates.cta.getStarted')}
          </ButtonLink>
        </CtaInner>
      </CtaBand>
    </Page>
  );
};

export default TemplatesPage;
